import { useConnectToken, useExchangeCode } from '@/query/google';
import { OAuthStatusResponse } from '@firestore-gui/shared';
import { useGoogleLogin } from '@react-oauth/google';
import { useCallback, useState } from 'react';
import { useToast } from '../ui/toast';
import { getOAuthErrorToast, shouldShowBanner } from './google-cloud-connection-banner.domain';

interface UseGoogleCloudConnectionBannerArgs {
  oauthStatus: OAuthStatusResponse | null;
  onStatusChange: () => Promise<void>;
}

const OAUTH_SCOPES =
  'https://www.googleapis.com/auth/cloud-platform.read-only https://www.googleapis.com/auth/datastore';

export function useGoogleCloudConnectionBanner({
  oauthStatus,
  onStatusChange,
}: UseGoogleCloudConnectionBannerArgs) {
  const { toast } = useToast();
  const [isConnecting, setIsConnecting] = useState(false);
  const connectTokenMutation = useConnectToken();
  const exchangeCodeMutation = useExchangeCode();

  const googleLogin = useGoogleLogin({
    flow: 'auth-code',
    scope: OAUTH_SCOPES,
    onSuccess: useCallback(
      async (codeResponse: { code: string }) => {
        try {
          setIsConnecting(true);

          const connectTokenResponse = await connectTokenMutation.mutateAsync();
          const { connectToken } = connectTokenResponse;

          await exchangeCodeMutation.mutateAsync({
            code: codeResponse.code,
            connectToken,
            redirectUri: window.location.origin,
          });

          await onStatusChange();

          toast({
            title: 'Success',
            description: 'Google Cloud connected successfully!',
            variant: 'default',
          });
        } catch (error: unknown) {
          console.error('[OAuth] Error exchanging code:', error);
          const message =
            error instanceof Error ? error.message : 'Failed to complete Google Cloud connection';
          toast({
            title: 'Connection Failed',
            description: message,
            variant: 'destructive',
          });
        } finally {
          setIsConnecting(false);
        }
      },
      [connectTokenMutation, exchangeCodeMutation, onStatusChange, toast]
    ),
    onError: useCallback(
      (errorResponse: unknown) => {
        console.error('[OAuth] Google login error:', errorResponse);
        setIsConnecting(false);

        const { show, title, description } = getOAuthErrorToast(errorResponse);
        if (show) {
          toast({
            title,
            description,
            variant: 'destructive',
          });
        }
      },
      [toast]
    ),
  });

  const handleConnect = useCallback(() => {
    setIsConnecting(true);
    googleLogin();
  }, [googleLogin]);

  return {
    shouldShow: shouldShowBanner(oauthStatus),
    isConnecting,
    handleConnect,
  };
}
